import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable, map, of} from 'rxjs';
import * as gql from 'gql-query-builder';
import {BaseService} from '@shared/services/api/base-service';
import {PagedModel} from '@shared/api/model/paged-model';
import {HttpUtilityService} from '@shared/services/api/http-utility-service';
import {PageInfo} from '@shared/components/ace-table/ace-table.component';
import {APIResponse} from '@app/layout/admin/services/api/api.interface';
import {FAULT_MODES_OPERATION, FAULT_MODES_GRAPHQL_QUERY, FaultMode, FaultModeId, FaultModeSearchTerm, FaultModeCreateBody} from './models/fault-mode';
import {TEMPLATE_CATEGORIES_OPERATION, TEMPLATE_CATEGORIES_GRAPHQL_QUERY, TemplateCategory, TemplateCategoryCreateBody, TemplateCategoryId, TemplateCategorySearchTerm} from './models/template-category';
import {COMPONENTS_OPERATION, COMPONENTS_GRAPHQL_QUERY, TemplateComponent, TemplateComponentId, TemplateComponentSearchTerm, TemplateComponentCreateBody} from './models/template-component';
import {CollectionUtils} from '@shared/util/collections/collection-utils';
import {MetadataType} from '@app/graphql-typings/graphql';
import {I18nMessage} from '@app/shared/api/model/i18n';
import {MessageTranslateService} from '@app/shared/services';


@Injectable({providedIn: 'root'})
export class TemplateCategoriesService extends BaseService {
  private readonly headers: HttpHeaders = this.getHeaders();
  private readonly faultModesQuery = gql.query(FAULT_MODES_GRAPHQL_QUERY);
  private readonly templateCategoriesQuery = gql.query(TEMPLATE_CATEGORIES_GRAPHQL_QUERY);
  private readonly componentsQuery = gql.query(COMPONENTS_GRAPHQL_QUERY);
  private readonly PAGE_SIZE = 2000;

  constructor(private http: HttpClient,
              private httpUtilityService: HttpUtilityService,
              private messageTranslateService: MessageTranslateService) {
    super();
  }

  getFaultModes(pageInfo: PageInfo, searchTerm?: FaultModeSearchTerm): Observable<PagedModel<FaultMode[]>> {
    const variables = {
      start_index: pageInfo.first,
      limit: pageInfo.rows,
      ...searchTerm
    };
    return this.httpUtilityService.tasksGraphQL<FaultMode[]>(
      this.faultModesQuery,
      FAULT_MODES_OPERATION,
      variables
    );
  }

  createFaultMode(faultMode: FaultModeCreateBody): Observable<FaultMode> {
    return this.http
      .post(this.makeFaultModesPath(), [faultMode], {headers: this.headers})
      .pipe(map((res: APIResponse<{fault_modes: FaultMode[]}>) => {
        const faultModes = this.httpUtilityService.extractResponseData<FaultMode[]>(res, 'fault_modes');
        return CollectionUtils.only(faultModes);
      }));
  }

  updateFaultMode(faultMode: FaultMode): Observable<FaultMode> {
    return this.http
      .put(this.makeFaultModePath(faultMode.fault_mode_id), faultMode, {headers: this.headers})
      .pipe(map((res: APIResponse<{fault_mode: FaultMode}>) =>
          this.httpUtilityService.extractResponseData<FaultMode>(res, 'fault_mode')));
  }

  deleteFaultModeById(faultModeId: FaultModeId): Observable<void> {
    return this.http.delete<void>(this.makeFaultModePath(faultModeId), {headers: this.headers});
  }

  getComponents(pageInfo: PageInfo, searchTerm?: TemplateComponentSearchTerm): Observable<PagedModel<TemplateComponent[]>> {
    const variables = {
      start_index: pageInfo.first,
      limit: pageInfo.rows,
      ...searchTerm
    };
    return this.httpUtilityService.tasksGraphQL<TemplateComponent[]>(
      this.componentsQuery,
      COMPONENTS_OPERATION,
      variables
    );
  }

  createComponent(component: TemplateComponentCreateBody): Observable<TemplateComponent> {
    return this.http
      .post(this.makeComponentsPath(), [component], {headers: this.headers})
      .pipe(map((res: APIResponse<{components: TemplateComponent[]}>) => {
        const components = this.httpUtilityService.extractResponseData<TemplateComponent[]>(res, 'components');
        return CollectionUtils.only(components);
      }));
  }

  updateComponent(component: TemplateComponent): Observable<TemplateComponent> {
    return this.http
      .put(this.makeComponentPath(component.component_id), component, {headers: this.headers})
      .pipe(map((res: APIResponse<{component: TemplateComponent}>) =>
          this.httpUtilityService.extractResponseData<TemplateComponent>(res, 'component')));
  }

  deleteComponentById(componentId: TemplateComponentId): Observable<void> {
    return this.http.delete<void>(this.makeComponentPath(componentId), {headers: this.headers});
  }

  getTemplateCategories(pageInfo: PageInfo, searchTerm?: TemplateCategorySearchTerm | null): Observable<PagedModel<TemplateCategory[]>> {
    const variables = {
      start_index: pageInfo.first,
      limit: pageInfo.rows,
      ...searchTerm
    };
    return this.httpUtilityService.tasksGraphQL<TemplateCategory[]>(this.templateCategoriesQuery, TEMPLATE_CATEGORIES_OPERATION, variables);
  }

  createTemplateCategory(body: TemplateCategoryCreateBody): Observable<TemplateCategory> {
    return this.http
      .post(this.makeTemplateCategoriesPath(), [body], {headers: this.headers})
      .pipe(map((res: APIResponse<{template_categories: TemplateCategory[]}>) => {
        const categories = this.httpUtilityService.extractResponseData<TemplateCategory[]>(res, 'template_categories');
        return CollectionUtils.only(categories);
      }));
  }

  updateTemplateCategory(templateCategory: TemplateCategory): Observable<TemplateCategory> {
    return this.http
      .put(this.makeTemplateCategoryPath(templateCategory.template_category_id), templateCategory, {headers: this.headers})
      .pipe(map((res: APIResponse<{template_category: TemplateCategory}>) =>
          this.httpUtilityService.extractResponseData<TemplateCategory>(res, 'template_category')));
  }

  deleteTemplateCategoryById(templateCategoryId: TemplateCategoryId): Observable<void> {
    return this.http.delete<void>(this.makeTemplateCategoryPath(templateCategoryId), {headers: this.headers});
  }

  getAllFaultModes(): Observable<FaultMode[]> {
    return this.httpUtilityService.pageUntilComplete(
      (pageInfo) => this.getFaultModes(pageInfo),
      {first: 0, rows: this.PAGE_SIZE}
    );
  }

  getAllComponents(): Observable<TemplateComponent[]> {
    return this.httpUtilityService.pageUntilComplete(
      (pageInfo) => this.getComponents(pageInfo),
      {first: 0, rows: this.PAGE_SIZE}
    );
  }

  getAllTemplateCategories(searchTerm?: TemplateCategorySearchTerm | null): Observable<TemplateCategory[]> {
    return this.httpUtilityService.pageUntilComplete(
      (pageInfo) => this.getTemplateCategories(pageInfo, searchTerm),
      {first: 0, rows: this.PAGE_SIZE}
    );
  }

  makeCategoryDisplayName(metadataTypeCode: string, faultModeName: string, componentName: string | null): string {
    return `${metadataTypeCode} - ${componentName ? componentName + ' ' : ''}${faultModeName}`;
  }

  buildUnableToShareTemplateCategoryMessage(metadataType: MetadataType | null,
                                            faultMode: FaultMode | null,
                                            component: TemplateComponent | null): I18nMessage | null {
    const metadataTypeSharedOrNull = metadataType != null ? metadataType.shared : true;
    const componentSharedOrNull = component != null ? component.shared : true;
    const faultModeSharedOrNull = faultMode != null ? faultMode.shared : true;
    const unsharedItems = [
      ...!metadataTypeSharedOrNull ? [{messageKey: 'Metadata Type'}] : [],
      ...!componentSharedOrNull ? [{messageKey: 'Component'}] : [],
      ...!faultModeSharedOrNull ? [{messageKey: 'Fault Mode'}] : [],
    ];
    if (unsharedItems.length) {
      return {
        messageKey: 'The following items need to be shared in order to share this template category',
        parameters: {
          items: unsharedItems.map(item => this.messageTranslateService.instant(item)).join(', ')
        }
      };
    }
    return null;
  }

  private makeFaultModesPath(): string {
    return this.getGateway() + '/tasks/fault-modes';
  }

  private makeFaultModePath(id: FaultModeId): string {
    return this.makeFaultModesPath() + '/' + id;
  }

  private makeComponentsPath(): string {
    return this.getGateway() + '/tasks/components';
  }

  private makeComponentPath(id: TemplateComponentId): string {
    return this.makeComponentsPath() + '/' + id;
  }

  private makeTemplateCategoriesPath(): string {
    return this.getGateway() + '/tasks/templates/categories';
  }

  private makeTemplateCategoryPath(id: TemplateCategoryId): string {
    return this.makeTemplateCategoriesPath() + '/' + id;
  }
}
